import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";

const combosMatch = (a, b) => {
  const aKeys = Object.keys(a);
  return aKeys.length === Object.keys(b).length && aKeys.every((k) => a[k] === b[k]);
};

// Renders each option group (e.g. Colour, With or Without Glass) as a row of
// selectable pills. Reports the selected choices + live total price back to
// the parent via onChange, so the parent can update the displayed price and
// gate the Buy Now button until all required groups are chosen.
//
// `variantStock` handles stock that varies by the *combination* of two
// otherwise-independent groups (e.g. Black is in stock with or without glass,
// but Dark Grey is only in stock with glass) — something a per-choice
// `inStock` flag on a single group can't express on its own.
export const ProductOptions = ({ optionGroups, variantStock = [], basePrice, onChange }) => {
  const [selected, setSelected] = useState({});

  useEffect(() => {
    const delta = optionGroups.reduce((sum, group) => {
      const choice = group.choices.find((c) => c.label === selected[group.key]);
      return sum + (choice ? choice.priceDelta : 0);
    }, 0);
    const allRequiredSelected = optionGroups.every((g) => !g.required || selected[g.key]);
    const matchedOverride = allRequiredSelected
      ? variantStock.find((v) => combosMatch(v.combination, selected))
      : null;
    const combinationInStock = !matchedOverride || matchedOverride.inStock !== false;
    onChange({
      selectedOptions: selected,
      totalPrice: basePrice + delta,
      allRequiredSelected,
      combinationInStock,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selected)]);

  if (!optionGroups || optionGroups.length === 0) return null;

  const allRequiredSelected = optionGroups.every((g) => !g.required || selected[g.key]);
  const matchedOverride = allRequiredSelected ? variantStock.find((v) => combosMatch(v.combination, selected)) : null;
  const showCombinationWarning = matchedOverride && matchedOverride.inStock === false;

  return (
    <div className="flex flex-col gap-4 mb-2" data-testid="product-options">
      {optionGroups.map((group) => (
        <div key={group.key}>
          <p className="text-xs uppercase tracking-[0.12em] text-[var(--text-secondary)] mb-2">
            {group.label} {group.required && <span className="text-[var(--brand-primary)]">*</span>}
          </p>
          <div className="flex flex-wrap gap-2">
            {group.choices.map((choice) => {
              const isSelected = selected[group.key] === choice.label;
              const isOutOfStock = choice.inStock === false;
              return (
                <button
                  key={choice.label}
                  type="button"
                  disabled={isOutOfStock}
                  data-testid={`option-${group.key}-${choice.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => !isOutOfStock && setSelected((prev) => ({ ...prev, [group.key]: choice.label }))}
                  title={isOutOfStock ? `${choice.label} is currently out of stock` : undefined}
                  className={`px-4 py-2 text-sm border rounded-full transition-colors ${
                    isOutOfStock
                      ? "border-white/10 text-white/30 cursor-not-allowed line-through"
                      : isSelected
                      ? "bg-[var(--brand-primary)] text-black border-[var(--brand-primary)]"
                      : "border-white/25 text-white/80 hover:border-[var(--brand-primary)]/70"
                  }`}
                >
                  {choice.label}
                  {choice.priceDelta > 0 && ` (+$${choice.priceDelta})`}
                  {isOutOfStock && " — Out of Stock"}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {showCombinationWarning && (
        <p data-testid="product-options-combination-warning" className="flex items-center gap-2 text-sm text-amber-400">
          <AlertTriangle size={14} /> This exact combination is currently out of stock — please choose a different combination, or enquire below.
        </p>
      )}
    </div>
  );
};
